const capabilities = [
  { icon: 'ri-robot-line', label: 'AI Call Handling', desc: 'Never miss another opportunity', accent: '#2563EB' },
  { icon: 'ri-flashlight-line', label: 'Instant Lead Response', desc: 'Engage prospects within seconds', accent: '#22C55E' },
  { icon: 'ri-calendar-check-line', label: 'Automated Booking', desc: 'Fill your schedule without manual effort', accent: '#06B6D4' },
  { icon: 'ri-repeat-line', label: 'Smart Follow-Up', desc: 'Convert more estimates into jobs', accent: '#F97316' },
  { icon: 'ri-bar-chart-line', label: 'Pipeline Visibility', desc: 'Track performance in real time', accent: '#2563EB' },
];

const steps = [
  { num: '01', title: 'Capture every inbound lead', desc: 'Every call, form, and message is captured automatically — nothing slips through.' },
  { num: '02', title: 'Respond instantly using AI', desc: 'AI engages the prospect within seconds, 24/7, even when your team is on a job.' },
  { num: '03', title: 'Qualify and route automatically', desc: 'Leads are scored, qualified, and routed to the right technician or dispatcher.' },
  { num: '04', title: 'Book jobs directly into your calendar', desc: 'Confirmed appointments land in your dispatch calendar without manual input.' },
  { num: '05', title: 'Track and optimize performance', desc: 'Real-time dashboards show what\'s working and where to improve.' },
];

export default function SolutionSection() {
  return (
    <section id="how-it-works" className="bg-white py-28 md:py-36 overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 md:px-10">

        {/* Section header */}
        <div className="text-center mb-20">
          <p className="text-[#2563EB] text-xs font-semibold tracking-[0.2em] uppercase mb-5 font-inter">The System</p>
          <h2 className="text-5xl md:text-6xl font-black text-[#0F172A] leading-[1.05] tracking-tight mb-6 font-poppins">
            The PureHomeScale System
          </h2>
          <p className="text-[#334155]/50 text-lg max-w-xl mx-auto font-inter font-light">
            A fully automated growth engine designed specifically for home service operators.
          </p>
        </div>

        {/* Feature showcase — Apple-style large visual + list */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Left — Visual card */}
          <div className="relative bg-[#0F172A] rounded-3xl overflow-hidden min-h-[500px]">
            <img
              src="https://public.readdy.ai/ai/img_res/0f93bab820a047d89619578a2a3f9d22.jpg"
              alt="AI Growth Dashboard"
              className="w-full h-full object-cover object-top opacity-60"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0F172A] via-[#0F172A]/30 to-transparent"></div>
            <div className="absolute bottom-0 left-0 right-0 p-8">
              <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/15 rounded-full px-4 py-2 mb-4">
                <span className="w-1.5 h-1.5 rounded-full bg-[#22C55E] animate-pulse"></span>
                <span className="text-white/70 text-xs font-inter">System Active — 24/7</span>
              </div>
              <h3 className="text-white font-bold text-2xl font-poppins mb-2">Every lead. Every time.</h3>
              <p className="text-white/50 text-sm font-inter">Your AI growth engine never sleeps, never misses a call, never forgets to follow up.</p>
            </div>
          </div>

          {/* Right — Capabilities */}
          <div className="bg-[#F8FAFC] rounded-3xl p-8 md:p-10 flex flex-col justify-center">
            <h3 className="text-[#0F172A] font-bold text-xl mb-8 font-poppins">Core Capabilities</h3>
            <div className="space-y-1">
              {capabilities.map((c, i) => (
                <div
                  key={i}
                  className="group flex items-center gap-4 p-4 rounded-2xl hover:bg-white transition-all duration-300 cursor-default"
                >
                  <div
                    className="w-10 h-10 flex items-center justify-center rounded-xl flex-shrink-0 transition-all duration-300"
                    style={{ backgroundColor: `${c.accent}15` }}
                  >
                    <i className={`${c.icon} text-base`} style={{ color: c.accent }}></i>
                  </div>
                  <div className="flex-1">
                    <span className="text-[#0F172A] font-semibold text-sm font-poppins">{c.label}</span>
                    <span className="text-[#334155]/40 text-sm font-inter"> — {c.desc}</span>
                  </div>
                  <i className="ri-arrow-right-s-line text-[#334155]/20 group-hover:text-[#334155]/50 transition-colors"></i>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* How It Works — full-width dark card */}
        <div className="bg-[#0F172A] rounded-3xl p-8 md:p-12 overflow-hidden relative">
          <div className="absolute top-0 right-0 w-[400px] h-[300px] bg-[#2563EB]/10 blur-[80px] rounded-full pointer-events-none"></div>

          <div className="relative z-10">
            <div className="flex items-center gap-3 mb-10">
              <div className="w-2 h-2 rounded-full bg-[#06B6D4]"></div>
              <span className="text-white/40 text-xs font-semibold tracking-[0.2em] uppercase font-inter">How It Works</span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
              {steps.map((s, i) => (
                <div key={i} className="relative">
                  {/* Connector line */}
                  {i < steps.length - 1 && (
                    <div className="hidden lg:block absolute top-6 left-full w-full h-px bg-gradient-to-r from-[#2563EB]/30 to-transparent z-0"></div>
                  )}
                  <div className="relative z-10">
                    <div className="w-12 h-12 flex items-center justify-center rounded-2xl bg-[#2563EB]/15 border border-[#2563EB]/20 mb-4">
                      <span className="text-[#06B6D4] font-bold text-sm font-roboto">{s.num}</span>
                    </div>
                    <h4 className="text-white font-semibold text-sm mb-2 font-poppins leading-snug">{s.title}</h4>
                    <p className="text-white/35 text-xs leading-relaxed font-inter">{s.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
